import { useEffect, useMemo, useRef } from 'react'
import { Check, Clock, Crown, Mail, MapPin, Phone, Plane, Trash2, X } from 'lucide-react'
import { useEventPlannerStore } from '../store/eventPlannerStore'
import { formatDateTime } from '../data/formatTime'
import { project3DToScreen } from '@/lib/utils/project-to-screen'
import { placementScenePosition } from '../motion/placementPose'
import type { MovementPlan } from '../motion/movementPlan'
import type { ArrivalInquiry, EventContact, FlightStatus, ScheduleConfidence } from '../engine/types'

interface EventAircraftPopoverProps {
  map: mapboxgl.Map | null
  mapCenter: { lat: number; lng: number } | undefined
  plans: Record<string, MovementPlan>
}

const STATUS_STYLES: Record<FlightStatus, { label: string; className: string }> = {
  on_time: { label: 'On time', className: 'bg-emerald-500/15 text-emerald-300' },
  delayed: { label: 'Delayed', className: 'bg-amber-500/15 text-amber-300' },
  early: { label: 'Early', className: 'bg-sky-500/15 text-sky-300' },
}

const CONFIDENCE_STYLES: Record<ScheduleConfidence, { label: string; className: string }> = {
  confirmed: { label: 'Confirmed', className: 'bg-emerald-500/10 text-emerald-300/90' },
  estimated: { label: 'Estimated', className: 'bg-sky-500/10 text-sky-300/90' },
  predicted: { label: 'Predicted', className: 'bg-white/10 text-white/60' },
}

const ROLE_STYLES: Record<EventContact['role'], string> = {
  Primary: 'bg-amber-500/15 text-amber-300',
  Ops: 'bg-sky-500/15 text-sky-300',
  Billing: 'bg-violet-500/15 text-violet-300',
}

/** Minutes delta between an estimated and planned time, signed. */
function deltaMinutes(estimate: number, planned: number): number {
  return Math.round((estimate - planned) / 60000)
}

/** "+18m late" / "12m early" / "on time" */
function deltaLabel(estimate: number, planned: number): string {
  const m = deltaMinutes(estimate, planned)
  if (m > 0) return `+${m}m late`
  if (m < 0) return `${-m}m early`
  return 'on time'
}

function TimeRow({
  label,
  planned,
  estimate,
}: {
  label: string
  planned: number
  estimate: number
}) {
  const m = deltaMinutes(estimate, planned)
  const tone = m > 0 ? 'text-amber-300' : m < 0 ? 'text-sky-300' : 'text-emerald-300'
  return (
    <div className="space-y-0.5">
      <div className="text-[9px] font-semibold uppercase tracking-wider text-white/30">{label}</div>
      <div className="text-white/70">{formatDateTime(planned)}</div>
      <div className={`flex items-center gap-1 ${tone}`}>
        <Clock className="h-3 w-3" />
        <span>{formatDateTime(estimate)}</span>
        <span className="text-[9px] text-white/40">({deltaLabel(estimate, planned)})</span>
      </div>
    </div>
  )
}

function ContactRow({ c }: { c: EventContact }) {
  return (
    <div className="rounded-md bg-white/5 px-2 py-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-xs font-medium text-white/85">{c.name}</span>
        <span className={`rounded px-1 text-[9px] font-semibold uppercase ${ROLE_STYLES[c.role]}`}>
          {c.role}
        </span>
      </div>
      <div className="mt-0.5 flex flex-col gap-0.5 text-[10px] text-white/50">
        <a href={`mailto:${c.email}`} className="flex items-center gap-1 hover:text-white/80">
          <Mail className="h-2.5 w-2.5" /> <span className="truncate">{c.email}</span>
        </a>
        <a href={`tel:${c.phone}`} className="flex items-center gap-1 hover:text-white/80">
          <Phone className="h-2.5 w-2.5" /> {c.phone}
        </a>
      </div>
    </div>
  )
}

/**
 * Plane-anchored info popover for the selected event aircraft.
 *
 * Reads the selected placement + its inquiry, then floats above the plane's
 * projected screen position. Positioning is imperative (a RAF loop reading the
 * live store + camera) so it stays pinned during pan and taxiing with no React
 * re-renders driving the transform. Content shows planned vs estimated
 * (FlightAware-style) times, status/confidence, services, and company contacts.
 */
export function EventAircraftPopover({ map, mapCenter, plans }: EventAircraftPopoverProps) {
  const selectedId = useEventPlannerStore((s) => s.selectedAllocationId)
  const placements = useEventPlannerStore((s) => s.placements)
  const inquiriesById = useEventPlannerStore((s) => s.inquiriesById)
  const zones = useEventPlannerStore((s) => s.zones)

  const select = useEventPlannerStore((s) => s.select)
  const confirmAircraft = useEventPlannerStore((s) => s.confirmAircraft)
  const reassign = useEventPlannerStore((s) => s.reassign)

  const containerRef = useRef<HTMLDivElement | null>(null)

  const zoneName = useMemo(() => {
    const m = new Map(zones.map((z) => [z.areaId, z.name]))
    return (areaId: string) => m.get(areaId) ?? areaId
  }, [zones])

  const placement = selectedId ? placements[selectedId] : null
  const inq: ArrivalInquiry | undefined = placement ? inquiriesById[placement.scheduleId] : undefined

  // Imperative anchor — keep the popover pinned above the (possibly moving) plane.
  useEffect(() => {
    const el = containerRef.current
    if (!el || !map || !mapCenter || !placement) return

    let raf = 0
    const update = () => {
      const store = useEventPlannerStore.getState()
      const p = store.placements[placement.eventAllocationId]
      if (!p) {
        el.style.display = 'none'
        raf = requestAnimationFrame(update)
        return
      }
      const pos = placementScenePosition(p, plans, store.currentTime, store.motionConfig.enableAnimation)
      const screen = project3DToScreen(pos, map, mapCenter, -40)
      if (screen.visible) {
        el.style.display = 'block'
        el.style.left = `${screen.x}px`
        el.style.top = `${screen.y}px`
      } else {
        el.style.display = 'none'
      }
      raf = requestAnimationFrame(update)
    }
    raf = requestAnimationFrame(update)
    return () => cancelAnimationFrame(raf)
  }, [map, mapCenter, plans, placement])

  if (!selectedId || !placement) return null

  const status = inq ? STATUS_STYLES[inq.arrivalStatus] : null
  const confidence = inq ? CONFIDENCE_STYLES[inq.confidence] : null

  const hasContacts = !!inq && inq.contacts.length > 0

  return (
    <div
      ref={containerRef}
      className="pointer-events-auto absolute z-10 w-[40rem] max-w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-full"
      style={{ left: 0, top: 0 }}
    >
      <div className="rounded-xl border border-white/10 bg-slate-950/95 p-4 shadow-2xl backdrop-blur-md">
        {/* Header */}
        <div className="mb-3 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 text-base font-bold text-white">
              {placement.tailNumber}
              {inq?.isVip && <Crown className="h-3.5 w-3.5 text-amber-300" />}
            </div>
            <div className="flex items-center gap-1.5 text-xs text-white/50">
              <span>{placement.typeCode}</span>
              {inq?.callsign && <span className="text-white/30">· {inq.callsign}</span>}
            </div>
            {status && (
              <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase ${status.className}`}>
                {status.label}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => select(null)}
            className="flex h-6 w-6 items-center justify-center rounded text-white/40 hover:bg-white/10 hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Two-column body: schedule/meta on the left, contacts on the right */}
        <div className="flex gap-4">
          {/* Left column */}
          <div className="min-w-0 flex-1 space-y-3">
            {/* Operator + origin + zone */}
            <div className="space-y-1 text-xs text-white/70">
              {inq && (
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate">{inq.operatorName}</span>
                  {inq.originIcao && (
                    <span className="flex shrink-0 items-center gap-1 text-white/40">
                      <Plane className="h-3 w-3" /> from {inq.originIcao}
                    </span>
                  )}
                </div>
              )}
              <div className="flex items-center gap-1 text-white/50">
                <MapPin className="h-3 w-3 shrink-0" /> {zoneName(placement.areaId)} · lane {placement.laneIndex + 1}
              </div>
            </div>

            {/* Planned vs estimated (FlightAware) times */}
            <div className="rounded-lg border border-white/5 bg-white/[0.03] p-2.5">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-[9px] font-semibold uppercase tracking-wider text-white/40">
                  Planned · Est (FlightAware)
                </span>
                {confidence && (
                  <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase ${confidence.className}`}>
                    {confidence.label}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <TimeRow
                  label="Arrival"
                  planned={inq?.arrival ?? placement.arrival}
                  estimate={inq?.estimatedArrival ?? placement.arrival}
                />
                <TimeRow
                  label="Departure"
                  planned={inq?.departure ?? placement.departure}
                  estimate={inq?.estimatedDeparture ?? placement.departure}
                />
              </div>
            </div>

            {/* Services */}
            {inq && inq.services.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {inq.services.map((s, i) => (
                  <span key={i} className="rounded bg-sky-500/15 px-1 text-[9px] uppercase text-sky-300">
                    {String(s.type)}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Right column: contacts */}
          {hasContacts && (
            <div className="w-60 shrink-0 space-y-1">
              <div className="text-[9px] font-semibold uppercase tracking-wider text-white/40">
                Company contacts
              </div>
              {inq!.contacts.map((c, i) => (
                <ContactRow key={i} c={c} />
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={() => confirmAircraft(selectedId)}
            disabled={placement.confirmed}
            className="flex flex-1 items-center justify-center gap-1 rounded-md bg-emerald-500/20 px-2 py-1.5 text-xs font-medium text-emerald-200 hover:bg-emerald-500/30 disabled:opacity-60"
          >
            <Check className="h-3.5 w-3.5" /> {placement.confirmed ? 'Confirmed' : 'Confirm'}
          </button>
          <button
            type="button"
            onClick={() => reassign(selectedId)}
            className="flex items-center justify-center gap-1 rounded-md bg-white/10 px-2 py-1.5 text-xs font-medium text-white/70 hover:bg-white/20"
            title="Remove from this spot (frees it)"
          >
            <Trash2 className="h-3.5 w-3.5" /> Unplace
          </button>
        </div>
      </div>

      {/* Caret pointing down at the plane */}
      <div className="mx-auto h-0 w-0 border-x-8 border-t-8 border-x-transparent border-t-slate-950/95" />
    </div>
  )
}
